class SmartSearch extends HTMLElement {
    constructor() {
        super();

        this.euroFormatter = new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
        });

        this.state = {
            lastSearchInput: null,
        };

        this.config = {
            minSearchLength: 2,
            modelsLimit: 5,
            articlesLimit: 5,
            suggestionsLimit: 5,
        };

        this.initializeEvents();
    }

    initializeEvents() {
        this.searchInput.addEventListener("input", () => {
            const inputTime = (this.state.lastSearchInput = Date.now());

            setTimeout(() => {
                if (inputTime === this.state.lastSearchInput) {
                    this.search(this.searchInput.value);
                }
            }, 400);
        });

        this.searchInput.addEventListener("focus", () => {
            if (this.searchInput.value.length >= this.config.minSearchLength) {
                this.show();
            }
        });

        this.backdropElement.addEventListener("click", () => this.hide());

        this.searchIconSubmit.addEventListener("click", (e) => {
            e.preventDefault();
            this.formElement.submit();
        });

        this.formElement.addEventListener("submit", (e) => {
            e.preventDefault();
            this.submit();
        });
    }

    /**
     * @param {string} sSearch
     * @returns {Promise<void>}
     */
    async search(sSearch) {
        if (sSearch.length >= this.config.minSearchLength) {
            const [printers, products, autocompletes] = await Promise.all([
                PrintersApi.searchModels({ sSearch, limit: this.config.modelsLimit }),
                PrintersApi.searchArticles({ sSearch, limit: this.config.articlesLimit, reverseTypeSorting: sSearch.toLowerCase().split(/[\s]/g).includes("hp") }),
                PrintersApi.searchAutocomplete({ sSearch, limit: this.config.suggestionsLimit }),
            ]);

            this.clearSearchResults();
            this.addPrinterSearchResults(printers.results);
            this.addProductSearchResults(products.results);
            this.addSuggestionSearchResults(autocompletes.results, autocompletes.keywords);

            if (printers.results.length || products.results.length || autocompletes.results.length) {
                this.show();
            } else {
                this.hide();
            }
        } else {
            this.hide();
        }
    }

    hideBackdrop() {
        this.backdropElement.classList.remove("visible");
    }

    showBackdrop() {
        this.backdropElement.classList.add("visible");
    }

    clearSearchResults() {
        this.searchResultSuggestions.innerHTML = "";
        this.searchResultProducts.innerHTML = "";
        this.searchResultPrinters.innerHTML = "";
    }

    /**
     * @param {string[]} suggestions
     * @param {string[]} keywords
     */
    addSuggestionSearchResults(suggestions, keywords) {
        this.searchResultSuggestionsContainer.style.display = suggestions.length > 0 ? "block" : "none";

        for (const suggestion of suggestions) {
            const element = this.querySelector("#search-result-suggestion").content.cloneNode(true);

            const html = suggestion
                .split(" ")
                .map((part) => {
                    const starting = keywords.find((k) => part.startsWith(k));

                    if (keywords.includes(part)) {
                        return part;
                    } else if (starting) {
                        return `${starting}<b>${part.replace(starting, "")}</b>`;
                    } else {
                        return `<b>${part}</b>`;
                    }
                })
                .join(" ");

            element.querySelector("a").innerHTML = html;

            const url = new URL(this.formElement.action);
            url.searchParams.set("q", html.replace(/<[^>]+>/g, ""));
            element.querySelector("a").href = url.toString();

            this.searchResultSuggestions.appendChild(element);
        }
    }

    /** @param {object[]} products */
    addProductSearchResults(products) {
        this.searchResultProductsContainer.style.display = products.length > 0 ? "block" : "none";

        for (const product of products) {
            const element = this.querySelector("#search-result-product").content.cloneNode(true);

            if (product.media_url) {
                element.querySelector("img").src = product.media_url;
            }
            element.querySelector(".product-title-container a").innerText = product.name;
            element.querySelector(".product-title-container a").href = product.path;
            element.querySelector(".product-price").innerText = `${this.euroFormatter.format(product.price)} **`;
            element.querySelector("input[name='product-id']").value = product.id;
            element.querySelector("input[name='id']").value = product.variant_id;

            this.searchResultProducts.appendChild(element);
        }
    }

    /** @param {object[]} printers */
    addPrinterSearchResults(printers) {
        this.searchResultPrintersContainer.style.display = printers.length > 0 ? "block" : "none";

        for (const printer of printers) {
            const element = this.querySelector("#search-result-printer").content.cloneNode(true);

            element.querySelector("a").href = printer.path;
            element.querySelector("span.printer-name").innerText = printer.full_name;

            this.searchResultPrinters.appendChild(element);
        }
    }

    show() {
        this.searchResultsContainer.style.display = "block";
        this.showBackdrop();
    }

    hide() {
        this.searchResultsContainer.style.display = "none";
        this.hideBackdrop();
    }

    /** @returns {HTMLDivElement} */
    get searchResultsContainer() {
        return this.querySelector("div.search-results-container");
    }

    /** @returns {HTMLDivElement} */
    get searchResultSuggestions() {
        return this.searchResultSuggestionsContainer.querySelector("div.search-results--items");
    }

    /** @returns {HTMLDivElement} */
    get searchResultSuggestionsContainer() {
        return this.querySelector("div.search-results--suggestions");
    }

    /** @returns {HTMLDivElement} */
    get searchResultProducts() {
        return this.searchResultProductsContainer.querySelector("div.search-results--items");
    }

    /** @returns {HTMLDivElement} */
    get searchResultProductsContainer() {
        return this.querySelector("div.search-results--products");
    }

    /** @returns {HTMLDivElement} */
    get searchResultPrinters() {
        return this.searchResultPrintersContainer.querySelector("div.search-results--items");
    }

    /** @returns {HTMLDivElement} */
    get searchResultPrintersContainer() {
        return this.querySelector("div.search-results--printers");
    }

    /** @returns {HTMLInputElement} */
    get searchInput() {
        return this.querySelector("form input.header-search-input");
    }

    /** @returns {HTMLDivElement} */
    get backdropElement() {
        return document.body.querySelector("#smart-search-backdrop");
    }

    /** @returns {HTMLFormElement} */
    get formElement() {
        return this.querySelector("form");
    }

    /** @returns {HTMLAnchorElement} */
    get searchIconSubmit() {
        return this.formElement.querySelector("a.search-icon-submit");
    }

    submit() {
        if (this.searchInput.value.length < this.config.minSearchLength) {
            return;
        }
        const url = new URL(this.formElement.action);

        url.searchParams.set("q", this.searchInput.value);

        window.location.href = url.toString();
    }
}
customElements.define("smart-search", SmartSearch);
